import { __awaiter } from "tslib";
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path builder for the recommendation resource under a collection. */
const recommendationPath = (collection) => `/collections/${collection}/recommendation`;
/** Path builder for the model resource under a collection. */
const modelPath = (collection) => `/collections/${collection}/model`;
/** Shared tag callback: invalidates / provides the recommendation list for a collection. */
const listTagByCollection = (_result, _error, { collection }) => [
    { type: CC_API_TAGS.ProductRecommendationList, id: collection },
];
/**
 * Product recommendation changes affect Filter Set usage state on the manager page.
 * Invalidate the shared FilterSetList tag so active list queries re-fetch.
 */
const recommendationAndFilterSetListTags = (_result, _error, { collection }) => [
    ...listTagByCollection(_result, _error, { collection }),
    CC_API_TAGS.FilterSetList,
];
/** Shared tag callback: invalidates the recommendation list + a single recommendation. */
const listAndItemTags = (_result, _error, { collection, id }) => [
    { type: CC_API_TAGS.ProductRecommendationList, id: collection },
    { type: CC_API_TAGS.ProductRecommendation, id },
];
/** Shared tag callback: invalidates recommendation item/list tags and the Filter Set list. */
const listItemAndFilterSetListTags = (_result, _error, { collection, id }) => [
    ...listAndItemTags(_result, _error, { collection, id }),
    CC_API_TAGS.FilterSetList,
];
/**
 * RTK Query endpoints for product recommendation data.
 *
 * This module extends the base ccApi with product recommendation CRUD
 * endpoints and the companion model-type list query.
 *
 * All endpoints are collection-scoped: cache tags are tagged with
 * `{ type, id: collection }` so switching collections reads a fresh cache.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const productRecommendationsApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the full list of product recommendations for a collection.
         *
         * @endpoint GET /collections/{collection}/recommendation
         */
        getProductRecommendations: builder.query({
            query: ({ collection }) => recommendationPath(collection),
            providesTags: listTagByCollection,
        }),
        /**
         * Fetches a single product recommendation by ID within a collection.
         *
         * @endpoint GET /collections/{collection}/recommendation/{id}
         */
        getProductRecommendationById: builder.query({
            query: ({ collection, id }) => `${recommendationPath(collection)}/${id}`,
            providesTags: (_result, _error, { id }) => [{ type: CC_API_TAGS.ProductRecommendation, id }],
        }),
        /**
         * Fetches the list of recommendation model types for a collection.
         *
         * @endpoint GET /collections/{collection}/model
         */
        getProductRecommendationsModelTypeList: builder.query({
            query: ({ collection }) => modelPath(collection),
            providesTags: (_result, _error, { collection }) => [
                { type: CC_API_TAGS.ProductRecommendationModelTypeList, id: collection },
            ],
        }),
        // -----------------------------------------------------------------------
        // Mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new product recommendation in the given collection.
         *
         * @endpoint POST /collections/{collection}/recommendation
         */
        createProductRecommendation: builder.mutation({
            query: ({ collection, data }) => ({
                url: recommendationPath(collection),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: recommendationAndFilterSetListTags,
        }),
        /**
         * Updates an existing product recommendation.
         *
         * @endpoint PUT /collections/{collection}/recommendation/{id}
         */
        updateProductRecommendationById: builder.mutation({
            query: ({ collection, id, data }) => ({
                url: `${recommendationPath(collection)}/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: listItemAndFilterSetListTags,
        }),
        /**
         * Deletes a single product recommendation.
         *
         * @endpoint DELETE /collections/{collection}/recommendation/{id}
         */
        deleteProductRecommendation: builder.mutation({
            query: ({ collection, id }) => ({
                url: `${recommendationPath(collection)}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: listItemAndFilterSetListTags,
        }),
        /**
         * Bulk-deletes product recommendations by issuing individual DELETE calls.
         *
         * There is no bulk-delete API endpoint, so this uses a custom
         * `queryFn` with a `Promise.allSettled` pattern to preserve the
         * partial-success semantics of the original imperative client.
         *
         * @returns `{ succeeded, failed }` so consumers can report per-item errors.
         */
        bulkDeleteProductRecommendations: builder.mutation({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ collection, ids }, _queryApi, _extraOptions, baseQuery) {
                    const results = yield Promise.allSettled(ids.map((id) => Promise.resolve(baseQuery({
                        url: `${recommendationPath(collection)}/${id}`,
                        method: 'DELETE',
                    }))));
                    const succeeded = [];
                    const failed = [];
                    results.forEach((result, index) => {
                        var _a, _b;
                        if (result.status === 'fulfilled' && !result.value.error) {
                            succeeded.push(ids[index]);
                        }
                        else {
                            const errorMessage = result.status === 'rejected'
                                ? String(result.reason)
                                : JSON.stringify((_b = (_a = result.value.error) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : 'Unknown error');
                            failed.push({ id: ids[index], error: errorMessage });
                        }
                    });
                    return { data: { succeeded, failed } };
                });
            },
            invalidatesTags: (result, _error, arg) => (result && result.succeeded.length > 0
                ? [
                    { type: CC_API_TAGS.ProductRecommendationList, id: arg.collection },
                    CC_API_TAGS.FilterSetList,
                ]
                : []),
        }),
    }),
});
export const { useGetProductRecommendationsQuery, useGetProductRecommendationByIdQuery, useGetProductRecommendationsModelTypeListQuery, useCreateProductRecommendationMutation, useUpdateProductRecommendationByIdMutation, useDeleteProductRecommendationMutation, useBulkDeleteProductRecommendationsMutation, } = productRecommendationsApi;
